import { computed, makeAutoObservable } from 'mobx'
import { type DocStore } from 'stores/DocStore'
import { CommentItem, TodoItem, CollabItemType, CollabItem, TrackedChangeItem } from './DocStore.types'
import { Todo } from 'api/clients/TodoClient'
import { resolvedFilter, reviewFilter } from './CollabItemsFilters'
import { type Comment } from 'types'
import { InformalFeedbackRequest, InformalFeedbackRequestState } from 'api/clients/InformalFeedbackRequestsClient'
import { queryKey } from 'hooks/queries/flow/useDocFlowState'

export class CleanupMode {
  docStore: DocStore
  currentReview?: InformalFeedbackRequest = undefined
  cleanedUpCommentsCount: number = 0
  outstandingCommentsCount: number = 0
  cleanedUpTrackedChangesCount: number = 0
  outstandingTrackedChangesCount: number = 0
  cleanedUpTodosCount: number = 0
  outstandingTodosCount: number = 0
  currentReviewHasItems: boolean = false

  constructor(docStore: DocStore) {
    makeAutoObservable(this, {
      cleanedUpItemsCount: computed,
      outstandingItems: computed,
    })
    this.docStore = docStore
  }

  invalidateQuery() {
    this.docStore.rootStore.queryClient.invalidateQueries(queryKey(this.docStore.articleId, this.docStore.layerId))
  }

  cleanMany(itemType: CollabItemType, amount: number = 1) {
    Array(amount)
      .fill(itemType)
      .forEach(() => this.clean(itemType))
  }

  dirtyMany(itemType: CollabItemType, amount: number = 1) {
    Array(amount)
      .fill(itemType)
      .forEach(() => this.dirty(itemType))
  }

  clean(itemType: CollabItemType) {
    switch (itemType) {
      case CollabItemType.Comment:
        this.cleanedUpCommentsCount++
        return
      case CollabItemType.Todo:
        this.cleanedUpTodosCount++
        return
      case CollabItemType.TrackedChange:
        this.cleanedUpTrackedChangesCount++
        return
      default:
        return
    }
  }

  dirty(itemType: CollabItemType) {
    switch (itemType) {
      case CollabItemType.Comment:
        this.cleanedUpCommentsCount--
        return
      case CollabItemType.Todo:
        this.cleanedUpTodosCount--
        return
      case CollabItemType.TrackedChange:
        this.cleanedUpTrackedChangesCount--
        return
      default:
        return
    }
  }

  get cleanedUpItemsCount() {
    return this.cleanedUpCommentsCount + this.cleanedUpTrackedChangesCount + this.cleanedUpTodosCount
  }

  get outstandingItems() {
    return this.outstandingCommentsCount + this.outstandingTrackedChangesCount + this.outstandingTodosCount
  }

  markManyForCleaning(itemType: CollabItemType, amount: number = 1) {
    if (amount > 0)
      return Array(amount)
        .fill(itemType)
        .forEach(() => this.markForCleaning(itemType))
    if (amount < 0)
      return Array(-amount)
        .fill(itemType)
        .forEach(() => this.unmarkForCleaning(itemType))
  }

  markForCleaning(itemType: CollabItemType) {
    switch (itemType) {
      case CollabItemType.Comment:
        this.outstandingCommentsCount++
        return
      case CollabItemType.Todo:
        this.outstandingTodosCount++
        return
      case CollabItemType.TrackedChange:
        this.outstandingTrackedChangesCount++
        return
      default:
        return
    }
  }

  unmarkManyForCleaning(itemType: CollabItemType, amount: number = 1) {
    Array(amount)
      .fill(itemType)
      .forEach(() => this.unmarkForCleaning(itemType))
  }

  unmarkForCleaning(itemType: CollabItemType) {
    switch (itemType) {
      case CollabItemType.Comment:
        this.outstandingCommentsCount--
        return
      case CollabItemType.Todo:
        this.outstandingTodosCount--
        return
      case CollabItemType.TrackedChange:
        this.outstandingTrackedChangesCount--
        return
      default:
        return
    }
  }

  setCurrentReview = (review: InformalFeedbackRequest | undefined) => {
    this.currentReview = review
    this.currentReviewHasItems =
      this.collabTodosForCurrentReview.length + this.collabCommentsForCurrentReview.length > 0
  }

  resetCounters = () => {
    this.cleanedUpCommentsCount = 0
    this.cleanedUpTodosCount = 0
    this.cleanedUpTrackedChangesCount = 0
    this.outstandingCommentsCount =
      this.collabItemsForCleanup.filter((item) => item.type === CollabItemType.Comment).length || 0
    this.outstandingTodosCount =
      this.collabItemsForCleanup.filter((item) => item.type === CollabItemType.Todo).length || 0
    this.outstandingTrackedChangesCount =
      this.collabItemsForCleanup.filter((item) => item.type === CollabItemType.TrackedChange).length || 0
  }

  resolveAllComments = async () => {
    const comments = this.commentsForCleanup.map((comment) => comment.item as Comment)

    if (comments.length < 1) return

    try {
      this.cleanMany(CollabItemType.Comment, comments.length)
      await this.docStore.rootStore.comments.bulkResolve(comments)
    } catch (e) {
      this.dirtyMany(CollabItemType.Comment, comments.length)
      throw e
    }
  }

  completeAllTodos = async () => {
    const todos = this.todosForCleanup.map((todo) => todo.item as Todo)

    if (todos.length < 1) return

    try {
      this.cleanMany(CollabItemType.Todo, todos.length)
      await this.docStore.rootStore.todos.batchComplete(todos)
    } catch (e) {
      this.dirtyMany(CollabItemType.Todo, todos.length)
      throw e
    }
  }

  resolveAllCommentsAndTodos = async () => Promise.all([this.resolveAllComments(), this.completeAllTodos()])

  destroyAllComments = async () => {
    if (!this.docStore.layerId) return

    const comments = this.commentsForCleanup.map((comment) => comment.item as Comment)

    if (comments.length < 1) return

    try {
      this.cleanMany(CollabItemType.Comment, comments.length)
      await this.docStore.rootStore.comments.bulkDestroy(this.docStore.articleId, this.docStore.layerId, comments)
    } catch (e) {
      this.dirtyMany(CollabItemType.Comment, comments.length)
      throw e
    }
  }

  get commentsAsItems(): CollabItem[] {
    return this.docStore.comments.map((comment) => ({ type: CollabItemType.Comment, item: comment } as CommentItem))
  }

  // Non-resolved comments available for cleanup for a given Review if one is provided, all of them otherwise
  filteredComments = (review?: InformalFeedbackRequest) => {
    return this.docStore.comments
      .map((comment) => ({ type: CollabItemType.Comment, item: comment } as CommentItem))
      .filter((item) => !resolvedFilter.fn(item))
      .filter((item) => (review ? reviewFilter.fn(item, review) : true))
  }

  // Non-resolved todos available for cleanup for a given Review if one is provided, all of them otherwise
  filteredTodos = (review?: InformalFeedbackRequest) => {
    return this.docStore.todos
      .map((todo) => ({ type: CollabItemType.Todo, item: todo } as TodoItem))
      .filter((item) => !resolvedFilter.fn(item))
      .filter((item) => (review ? reviewFilter.fn(item, review) : true))
  }

  get commentsForCleanup(): CollabItem[] {
    const filteredCommentsForReview = this.filteredComments(this.currentReview)
    const reviewIsOpen = this.currentReview?.state === InformalFeedbackRequestState.Open
    const filteredComments =
      filteredCommentsForReview.length > 0 || reviewIsOpen || this.currentReviewHasItems
        ? filteredCommentsForReview
        : this.filteredComments()

    return filteredComments
  }

  get todosForCleanup(): CollabItem[] {
    const filteredTodosForReview = this.filteredTodos(this.currentReview)
    const reviewIsOpen = this.currentReview?.state === InformalFeedbackRequestState.Open
    const filteredTodos =
      filteredTodosForReview.length > 0 || reviewIsOpen || this.currentReviewHasItems
        ? filteredTodosForReview
        : this.filteredTodos()

    return filteredTodos
  }

  // All tracked changes available for cleanup
  get collabTrackedChanges(): CollabItem[] {
    return [
      ...this.docStore.trackedChanges.map(
        (change) => ({ type: CollabItemType.TrackedChange, item: change } as TrackedChangeItem),
      ),
    ]
  }

  // Items available for cleanup
  get filteredItems(): CollabItem[] {
    return [...this.commentsForCleanup, ...this.todosForCleanup, ...this.collabTrackedChanges]
  }

  get collabItemsForCleanup(): CollabItem[] {
    // If there are no items to clean for the current review, return them all
    const filteredItemsForReview = this.filteredItems
    const reviewIsOpen = this.currentReview?.state === InformalFeedbackRequestState.Open
    const filteredItems =
      filteredItemsForReview.length > 0 || reviewIsOpen ? filteredItemsForReview : this.filteredItems

    if (filteredItems.length <= 1) {
      this.invalidateQuery()
    }

    return filteredItems
  }

  get collabCommentsForCurrentReview(): CollabItem[] {
    return this.filteredComments(this.currentReview)
  }

  get collabTodosForCurrentReview(): CollabItem[] {
    return this.filteredTodos(this.currentReview)
  }

  get collabItemsForCurrentReview(): CollabItem[] {
    return [...this.collabCommentsForCurrentReview, ...this.collabTodosForCurrentReview]
  }

  ////////////////////////////////// Order items by UUID //////////////////////////////////

  get docWideCollabItems(): CollabItem[] {
    return this.collabItemsForCleanup
      .filter(({ item, type }) => type !== CollabItemType.TrackedChange && !item.important && !item.uuid)
      .concat(this.inlineDetachedCollabItems)
  }

  get inlineCollabItems(): CollabItem[] {
    return this.collabItemsForCleanup.filter(
      ({ item, type }) => Boolean(item.uuid) && (type === CollabItemType.TrackedChange || !item.important),
    )
  }

  get inlineAttachedCollabItems(): CollabItem[] {
    return Array.from(this.docStore.orderedCollabItemsUuids)
      .map((uuid) => this.inlineCollabItems.find(({ item }) => item.uuid === uuid))
      .filter((item) => item !== undefined) as CollabItem[]
  }

  get inlineDetachedCollabItems(): CollabItem[] {
    return this.inlineCollabItems.filter(
      ({ item }) => item.uuid && !this.docStore.orderedCollabItemsUuids.has(item.uuid),
    )
  }

  get orderedCollabItemsForCleanup(): CollabItem[] {
    return [...this.docWideCollabItems, ...this.inlineAttachedCollabItems] as CollabItem[]
  }
}

export enum CleanupModeActions {
  Delete = 'delete',
  Merge = 'merge',
}

export const CleanupModeActionParam = 'cleanupAction'
